import { Category } from "../../../services-hooks/types";
import React, { useEffect, useRef, useState } from "react";
import styles from "./radio-view.module.scss";
import MemoPause from "../icons/Pause";
import MemoPlay from "../icons/Play";
import MemoHeartOutline from "../icons/HeartOutline";
import {
  useGetPillowRadioHistory,
  useGetPillowRadioLive,
  useGetPillowRadioPodcasts,
  useGetPillowRadioSchedulesToday,
} from "../../../services-hooks/hooks";
import { playerActions, selectPlayerState } from "../../store/slices/player";
import { useAppDispatch, useAppSelector } from "../../store/store";
import clsx from "clsx";
import MemoLoader from "../icons/Loader";
import MemoLogo from "../icons/Logo";
import { useFavorites } from "../../hooks/favorites.hook";
import MemoHeartFull from "../icons/HeartFull";
import { FavoritesAuthGuardPopup } from "../auth/favorites-auth-guard.component";
import Loader from "../icons/Loader";
import { format, parseISO } from "date-fns";
import { usePlayerContext } from "app/context/player.context";
import { selectRadioState } from "app/store/slices/radio";
import Globe from "../icons/Globe";
import { useFeaturesContext } from "app/context/features.context";
import { useTranslate } from "app/hooks/lang";
import MemoAngleDown from "../icons/AngleDown";
import Link from "next/link";
import { useInterval } from "react-use";

const enum RadioTabs {
  Live = "Live",
  Schedule = "Schedule",
  Podcasts = "Podcasts",
}

interface RadioViewProps {
  radio: Category;
}

export const RadioView: React.FC<RadioViewProps> = ({ radio }) => {
  const { activeSection, activeIndex } = useAppSelector(selectRadioState);
  const {
    setPlayerEnabled,
    setIsPlaying,
    isMediaLoading,
    isPlaying,
    setFullscreenPlayerInitialyOpen,
  } = usePlayerContext();
  const { showRadioWebsite } = useFeaturesContext();
  const dispatch = useAppDispatch();
  const playerState = useAppSelector(selectPlayerState);
  //TODO Add handling of missing liveData (show message instead of play button)
  const { data: liveData, isLoading } = useGetPillowRadioLive(
    radio.permalink!,
    {
      isPlaying:
        playerState.activeMedia?.category?.permalink === radio.permalink,
    }
  );
  const { data: radioPodcastsData, isLoading: radioPodcastsLoading } =
    useGetPillowRadioPodcasts(radio.permalink!, {
      page: 1,
      pageSize: 100,
    });
  const {
    data: historyData,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useGetPillowRadioHistory(radio.permalink!);

  const { data: scheduleData, isLoading: scheduleLoading } =
    useGetPillowRadioSchedulesToday(radio.permalink!, {
      page: 1,
      pageSize: 100,
    });
  const [activeTab, setActiveTab] = useState(RadioTabs.Podcasts);
  const { toggleFavorite, loading, isFavoriteCategory } = useFavorites();
  const t = useTranslate();
  const [descriptionExpandable, setDescriptionExpandable] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const containerHeight = descriptionContainerRef.current?.clientHeight || 0;
    const descriptionHeight = descriptionRef.current?.clientHeight || 0;
    if (containerHeight > 0 && descriptionHeight > 0) {
      if (containerHeight < descriptionHeight) {
        setDescriptionExpandable(true);
      }
    }
  }, [radio.description]);
  const startLivePlayback = () => {
    setFullscreenPlayerInitialyOpen(true);

    setPlayerEnabled(true);
    if (playerState?.activeMedia?.category?.permalink !== radio?.permalink) {
      dispatch(
        playerActions.selectTrack({
          category: radio,
          podcast: null,
        })
      );
    } else {
      setIsPlaying(true);
    }
    if (
      activeSection[activeIndex] &&
      activeSection[activeIndex].permalink === radio.permalink
    ) {
      dispatch(
        playerActions.setRadioQueue({
          items: activeSection,
          activeIndex: activeIndex,
        })
      );
    } else {
      dispatch(
        playerActions.setRadioQueue({
          items: [radio],
          activeIndex: 0,
        })
      );
    }
  };
  const stopPlayback = () => {
    setIsPlaying(false);
  };

  const addToFavourites = () => {
    toggleFavorite(radio);
  };

  const formatScheduleTime = (start: string, end: string) => {
    return `${format(parseISO(start), "HH:mm")} - ${format(
      parseISO(end),
      "HH:mm"
    )}`;
  };

  // refresh history
  useInterval(() => {
    if (activeTab === RadioTabs.Live) {
      refetchHistory();
    }
  }, 30000);

  useEffect(() => {
    if (activeTab === RadioTabs.Live) {
      refetchHistory();
    }
  }, [activeTab]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.podcast_cover}>
        {radio?.logo || radio?.smallLogo ? (
          <img src={radio?.logo || radio?.smallLogo} alt={radio?.name} />
        ) : (
          <div className={styles.no_image}>
            <MemoLogo />
          </div>
        )}
        <div className={styles.podcast_details}>
          <h1>{radio?.name}</h1>
          <div className={styles.buttons_wrapper} data-navigate-row>
            {isLoading || isMediaLoading ? (
              <button className={styles.action_button}>
                <Loader />
              </button>
            ) : playerState.activeMedia?.category?.permalink ===
                radio.permalink && isPlaying ? (
              <button
                disabled={isLoading}
                className={clsx(styles.action_button, styles.play_button)}
                onClick={stopPlayback}
                data-navigate-item
              >
                <MemoPause />
              </button>
            ) : (
              <button
                disabled={isLoading}
                className={clsx(styles.action_button, styles.play_button)}
                onClick={startLivePlayback}
                data-navigate-item
              >
                <MemoPlay />
              </button>
            )}

            <button
              className={clsx(styles.action_button, styles.favorites_button)}
              onClick={addToFavourites}
              data-navigate-item
            >
              {loading ? (
                <Loader />
              ) : isFavoriteCategory(radio) ? (
                <MemoHeartFull />
              ) : (
                <MemoHeartOutline />
              )}
            </button>
          </div>
        </div>
      </div>
      <div
        className={clsx(styles.podcast_description_container, {
          [styles.expanded]: descriptionExpanded,
          [styles.expandable]: descriptionExpandable,
        })}
      >
        <div className={styles.podcast_description}>
          <h2>{radio?.baseline}</h2>
          <div
            ref={descriptionContainerRef}
            className={styles.description_container}
          >
            <div ref={descriptionRef} className={styles.description}>
              {radio?.description}
            </div>
          </div>
        </div>
        {descriptionExpandable && (
          <div
            data-navigate-row
            className={clsx(styles.expander, {
              [styles.open]: descriptionExpanded,
            })}
          >
            <button
              data-navigate-item
              onClick={() => setDescriptionExpanded((value) => !value)}
            >
              <MemoAngleDown />
            </button>
          </div>
        )}
        {showRadioWebsite && radio.web && (
          <a href={radio.web} target="_blank" rel="noopener" data-navigate-item>
            <button className={styles.website_button}>
              <Globe />
              {t("radio.visit-website")}
            </button>
          </a>
        )}
      </div>
      <div className={styles.tabs_wrapper}>
        <div className={styles.tabs} data-navigate-row>
          <button
            onClick={() => setActiveTab(RadioTabs.Live)}
            className={clsx(styles.tab, {
              [styles.active]: activeTab === RadioTabs.Live,
            })}
            data-navigate-item
          >
            {t("tabs.playlist")}
          </button>

          <button
            onClick={() => setActiveTab(RadioTabs.Podcasts)}
            className={clsx(styles.tab, {
              [styles.active]: activeTab === RadioTabs.Podcasts,
            })}
            data-navigate-item
          >
            {t("tabs.podcasts")}
          </button>

          <button
            onClick={() => setActiveTab(RadioTabs.Schedule)}
            className={clsx(styles.tab, {
              [styles.active]: activeTab === RadioTabs.Schedule,
            })}
            data-navigate-item
          >
            {t("tabs.schedule")}
          </button>
        </div>
      </div>

      {activeTab === RadioTabs.Live ? (
        historyData?.data?.body?.content?.length ? (
          <div className={styles.content_wrapper}>
            <>
              <h2 className={styles.details_header}>
                {t("tabs.playlist")}
              </h2>
              {historyData?.data?.body?.content?.map(
                (historyItem, historyIndex) => (
                  <div
                    key={`history-item-${historyIndex}`}
                    className={clsx(styles.live_track, styles.responsive)}
                  >
                    <img
                      className={styles.live_track_logo}
                      src={
                        historyItem.cover &&
                        typeof historyItem.cover === "string"
                          ? historyItem.cover
                          : radio?.logo
                      }
                    />
                    <div className={styles.live_track_body}>
                      <h3>{historyItem?.name}</h3>
                      <h4>{historyItem?.artist?.name}</h4>
                    </div>
                  </div>
                )
              )}
            </>
          </div>
        ) : (
          <div className={styles.no_data}>
            {t("radio_playlist_message_noPlaylist")}
          </div>
        )
      ) : null}

      {activeTab === RadioTabs.Schedule ? (
        scheduleData &&
        scheduleData?.pages?.length &&
        scheduleData?.pages[0]?.data?.body?.content?.length ? (
          <div className={styles.content_wrapper}>
            <h2 className={styles.details_header}>{t("tabs.schedule")}</h2>

            {scheduleData?.pages[0]?.data?.body?.content?.map(
              (scheduleItem, scheduleIndex) => {
                return (
                  <div
                    key={`schedule-item-${scheduleIndex}`}
                    className={clsx(styles.live_track, styles.schedule_item)}
                    tabIndex={0}
                    data-navigate-row
                  >
                    {scheduleItem?.image || radio?.logo || radio?.smallLogo ? (
                      <img
                        className={styles.live_track_logo}
                        src={
                          scheduleItem?.image || radio?.logo || radio?.smallLogo
                        }
                      />
                    ) : (
                      <div
                        className={clsx(
                          styles.no_image,
                          styles.live_track_logo
                        )}
                      >
                        <MemoLogo />
                      </div>
                    )}

                    <div className={styles.live_track_body}>
                      <h3>{scheduleItem?.name}</h3>
                      {scheduleItem?.animators?.length ? (
                        <h4>
                          {scheduleItem?.animators?.map(
                            (animator, animatorIndex) => {
                              return (
                                <>
                                  <span key={`animator-${animatorIndex}`}>
                                    {animator?.name}
                                  </span>
                                  {animatorIndex !==
                                  scheduleItem.animators!.length - 1 ? (
                                    <span>,&nbsp;</span>
                                  ) : null}
                                </>
                              );
                            }
                          )}
                        </h4>
                      ) : null}
                      {scheduleItem.start && scheduleItem.end && (
                        <h5>
                          {formatScheduleTime(
                            scheduleItem.start,
                            scheduleItem.end
                          )}
                        </h5>
                      )}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        ) : (
          <div className={styles.no_data}>{t("radio.no-program")}</div>
        )
      ) : null}

      {activeTab === RadioTabs.Podcasts ? (
        radioPodcastsData?.pages?.length &&
        radioPodcastsData?.pages[0]?.data?.body?.content?.length ? (
          radioPodcastsLoading ? (
            <MemoLoader />
          ) : (
            <div className={styles.content_wrapper}>
              <h2 className={styles.details_header}>{t("tabs.podcasts")}</h2>
              {radioPodcastsData?.pages[0]?.data?.body?.content?.map(
                (radioPodcast, podcastIndex) => (
                  <div
                    data-navigate-row
                    key={`podcast-item-${radioPodcast.permalink}-${podcastIndex}`}
                  >
                    <Link href={`/${radioPodcast.permalink}`} passHref>
                      <a
                        key={`podcast-item-${podcastIndex}`}
                        className={styles.live_track}
                        tabIndex={0}
                        data-navigate-item
                      >
                        <img
                          className={styles.live_track_logo}
                          src={radioPodcast?.logo || radioPodcast?.smallLogo}
                          alt={radioPodcast.name}
                        />
                        <div className={styles.live_track_body}>
                          <h3>{radioPodcast?.name}</h3>
                          <h4>{radioPodcast?.description}</h4>
                        </div>
                      </a>
                    </Link>
                  </div>
                )
              )}
            </div>
          )
        ) : (
          <div className={styles.no_data}>{t("radio.no-podcasts")}</div>
        )
      ) : null}
    </div>
  );
};
